import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Управление доступом к отчётам.
// Список отчётов, у каждого кнопка «Настроить доступ» → модалка под этот отчёт:
// вкл/выкл ограничение + выбор адвокатов (поиск + фильтр по отделу).
// Модалка жёстко вписана в экран (max-h 85vh), внутри одна прокрутка со списком.
//
// props:
//   forms:     [{ id, form_name, short_name, form_type, restricted, form_accesses: [{ user_id }] }]
//   divisions: [{ id, name, users: [{ id, realname, division_id }] }]
const ROW_CAP = 200; // сколько строк рендерим максимум (чтобы не тормозило)

export default function FormAccessManager({ forms, divisions }) {
  const [formList, setFormList] = useState(forms || []);
  const [openFormId, setOpenFormId] = useState(null);

  const [search, setSearch] = useState("");
  const [divisionFilter, setDivisionFilter] = useState("");
  const [onlySelected, setOnlySelected] = useState(false);

  const allUsers = (divisions || []).flatMap((d) =>
    (d.users || []).map((u) => ({ ...u, divisionName: (d.name || "").trim() }))
  );

  const allowedIds = (form) =>
    form ? (form.form_accesses || []).map((fa) => fa.user_id) : [];

  const replaceForm = (updatedForm) =>
    setFormList((prev) =>
      prev.map((f) => (f.id === updatedForm.id ? updatedForm : f))
    );

  const openForm = formList.find((f) => f.id === openFormId);

  // ── API ──────────────────────────────────────────────────────────────
  const toggleRestriction = async (form, restricted) => {
    try {
      const r = await fetch("/settings/update_form_restriction", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ form_id: form.id, restricted }),
      });
      if (r.ok) {
        replaceForm(await r.json());
        toast.success(restricted ? "Доступ ограничен" : "Форма открыта всем");
      } else toast.error("Не удалось изменить ограничение.");
    } catch (e) {
      console.error(e);
      toast.error("Ошибка запроса.");
    }
  };

  const postAccess = async (formId, userIds) => {
    const r = await fetch("/settings/update_form_access", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ form_id: formId, user_ids: userIds }),
    });
    if (r.ok) {
      replaceForm(await r.json());
      return true;
    }
    return false;
  };

  const toggleUser = async (form, userId) => {
    const cur = allowedIds(form);
    const next = cur.includes(userId)
      ? cur.filter((id) => id !== userId)
      : [...cur, userId];
    if (await postAccess(form.id, next)) toast.success("Доступ обновлён");
    else toast.error("Не удалось обновить доступ.");
  };

  const setManyOnForm = async (form, userIds, grant) => {
    const cur = allowedIds(form);
    const idSet = new Set(userIds);
    const next = grant
      ? Array.from(new Set([...cur, ...userIds]))
      : cur.filter((id) => !idSet.has(id));
    if (await postAccess(form.id, next)) toast.success("Доступ обновлён");
    else toast.error("Не удалось обновить доступ.");
  };

  const openModal = (form) => {
    setOpenFormId(form.id);
    setSearch("");
    setDivisionFilter("");
    setOnlySelected(false);
  };
  const closeModal = () => setOpenFormId(null);

  const matchUsers = (form) => {
    const allowed = new Set(allowedIds(form));
    const q = search.trim().toLowerCase();
    return allUsers.filter((u) => {
      if (divisionFilter && String(u.division_id) !== divisionFilter)
        return false;
      if (onlySelected && !allowed.has(u.id)) return false;
      if (q && !(u.realname || "").toLowerCase().includes(q)) return false;
      return true;
    });
  };

  const selectStyle =
    "border border-gray-300 rounded-md px-3 py-2 bg-white w-full";

  return (
    <div className="min-h-screen">
      <ToastContainer />
      <div className="container max-w-screen-2xl mx-auto bg-white rounded-lg shadow-md p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Доступ к отчётам
        </h1>
        <p className="text-gray-500 mb-6">
          Нажмите «Настроить доступ» у нужного отчёта — там включается
          ограничение и выбираются адвокаты, которым разрешено его сдавать.
        </p>

        <div className="border rounded-lg overflow-hidden">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50 text-left text-sm text-gray-600">
                <th className="p-3">Отчёт</th>
                <th className="p-3 w-56">Статус</th>
                <th className="p-3 w-52 text-right">Действие</th>
              </tr>
            </thead>
            <tbody>
              {formList.map((form) => (
                <tr key={form.id} className="border-t hover:bg-gray-50">
                  <td className="p-3">
                    <div className="font-medium">
                      {form.short_name || form.form_name}
                    </div>
                    {form.short_name && (
                      <div className="text-xs text-gray-400">
                        {form.form_name}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    {form.restricted ? (
                      <span className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                        Ограничен · {allowedIds(form).length} с доступом
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2 text-sm text-gray-500">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        Открыт всем
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => openModal(form)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                    >
                      Настроить доступ
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Модалка — жёстко в пределах экрана */}
      {openForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-3xl flex flex-col"
            style={{ maxHeight: "85vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Шапка (фикс) */}
            <div className="flex items-start justify-between p-4 border-b shrink-0">
              <div className="pr-4">
                <h2 className="text-lg font-bold leading-tight">
                  {openForm.short_name || openForm.form_name}
                </h2>
                <p className="text-sm text-gray-400">Настройка доступа</p>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none shrink-0"
              >
                ×
              </button>
            </div>

            {/* Тело (flex-col, занимает оставшееся; список — единственная прокрутка) */}
            <div className="flex flex-col min-h-0 flex-1 p-4">
              <label className="flex items-center gap-3 mb-3 p-3 bg-blue-50 border border-blue-100 rounded-lg cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  className="w-5 h-5"
                  checked={!!openForm.restricted}
                  onChange={(e) => toggleRestriction(openForm, e.target.checked)}
                />
                <span className="font-medium text-gray-800">
                  Ограничить доступ — сдают только выбранные адвокаты
                </span>
              </label>

              {!openForm.restricted ? (
                <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 shrink-0">
                  Этот отчёт сдают <b>все адвокаты</b>. Включите ограничение
                  выше, чтобы выбрать, кому разрешено.
                </div>
              ) : (
                (() => {
                  const matched = matchUsers(openForm);
                  const shown = matched.slice(0, ROW_CAP);
                  const allowed = new Set(allowedIds(openForm));
                  return (
                    <>
                      {/* Фильтры (фикс) */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 shrink-0">
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">
                            Поиск по ФИО
                          </label>
                          <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Начните вводить фамилию…"
                            className="border border-gray-300 rounded-md px-3 py-2 w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">
                            Отдел
                          </label>
                          <select
                            className={selectStyle}
                            value={divisionFilter}
                            onChange={(e) => setDivisionFilter(e.target.value)}
                          >
                            <option value="">Все отделы</option>
                            {(divisions || []).map((division) => (
                              <option key={division.id} value={division.id}>
                                {division.name.trim()}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Панель действий (фикс) */}
                      <div className="flex flex-wrap items-center justify-between gap-2 bg-gray-100 px-3 py-2 rounded-t-lg border border-b-0 shrink-0">
                        <div className="flex items-center gap-3 text-sm">
                          <span className="font-medium">
                            Найдено: {matched.length} · с доступом:{" "}
                            {allowed.size}
                          </span>
                          <label className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              className="w-4 h-4"
                              checked={onlySelected}
                              onChange={(e) => setOnlySelected(e.target.checked)}
                            />
                            только с доступом
                          </label>
                        </div>
                        <div className="space-x-3">
                          <button
                            className="text-blue-600 hover:underline text-sm"
                            onClick={() =>
                              setManyOnForm(
                                openForm,
                                matched.map((u) => u.id),
                                true
                              )
                            }
                          >
                            Выдать найденным ({matched.length})
                          </button>
                          <button
                            className="text-red-600 hover:underline text-sm"
                            onClick={() =>
                              setManyOnForm(
                                openForm,
                                matched.map((u) => u.id),
                                false
                              )
                            }
                          >
                            Снять
                          </button>
                        </div>
                      </div>

                      {/* Список — ЕДИНСТВЕННАЯ прокрутка, занимает остаток высоты */}
                      <div className="flex-1 min-h-0 overflow-y-auto border rounded-b-lg">
                        <table className="w-full border-collapse">
                          <thead className="sticky top-0 bg-gray-50">
                            <tr className="text-left text-sm text-gray-600">
                              <th className="p-2 w-10">№</th>
                              <th className="p-2">Адвокат</th>
                              <th className="p-2">Отдел</th>
                              <th className="p-2 w-20 text-center">Доступ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {shown.map((user, idx) => (
                              <tr
                                key={user.id}
                                className="border-t hover:bg-gray-50"
                              >
                                <td className="p-2 text-center text-gray-400">
                                  {idx + 1}
                                </td>
                                <td className="p-2">{user.realname}</td>
                                <td className="p-2 text-gray-500 text-sm">
                                  {user.divisionName}
                                </td>
                                <td className="p-2 text-center">
                                  <input
                                    type="checkbox"
                                    className="w-5 h-5"
                                    checked={allowed.has(user.id)}
                                    onChange={() => toggleUser(openForm, user.id)}
                                  />
                                </td>
                              </tr>
                            ))}
                            {matched.length === 0 && (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="p-4 text-center text-gray-400"
                                >
                                  Никого не найдено по фильтру.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        {matched.length > ROW_CAP && (
                          <div className="p-3 text-center text-sm text-amber-600 bg-amber-50 border-t">
                            Показаны первые {ROW_CAP} из {matched.length}.
                            Уточните поиск или выберите отдел. Кнопка «Выдать
                            найденным» применяется ко всем {matched.length}.
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()
              )}
            </div>

            {/* Низ (фикс) */}
            <div className="p-3 border-t text-right shrink-0">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Готово
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
